import moment from 'moment';
import * as fileSaver from 'file-saver';
import { calculatePercentageDiscount } from 'Shared/Utils';
import {
  REACT_APP_STRIPE_CHARGE_PERCENT,
  REACT_APP_STRIPE_CHARGE_FIXED_CENTS,
  REACT_APP_CREDIT_CHARGE_PERCENT,
  REACT_APP_CREDIT_CHARGE_FIXED_CENTS,
  REACT_APP_DEBIT_CHARGE_PERCENT,
  REACT_APP_DEBIT_CHARGE_FIXED_CENTS,
} from 'global-constants/constants';

export const debounce = (func, wait = 400) => {
  let timeout;

  return (event) => {
    const { name, value } = event.target;
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => {
      timeout = null;
      func(name, value);
    }, wait);
  };
};

export const appendURLProtocol = (url = '') => {
  const isValidURL = /^((http|https):\/\/)/.test(url);

  if (!url || isValidURL) {
    return url;
  }

  return `https://${url}`;
};

// Format to milliseconds - Used to pass in request payload
export const formatToMilliSeconds = (date) => moment.utc(date).unix();

// Format to date string - Used to pass in request payload
export const formatToDateString = (date, requiredFormat = 'yyyy-MM-DD') =>
  moment(date).format(requiredFormat);

// 1645000000 to Wed Feb 16 2022 21:53:44 GMT+0530 (India Standard Time) {} - Used to prefill forms
export const formatToDatepickerInput = (date) => moment.utc(date);

// 1645000000 to Wed Feb 16 2022 21:53:44 GMT+0530 (India Standard Time) {} - Used to prefill forms
export const formatDobToDatepicker = (date = '') =>
  moment(date).isValid() ? moment(date).format('YYYY-MM-DDTHH:mm:ss') : null;

// Format milliseconds / MM-DD-yyyy to required format - Used to display in UI as UTC
export const formatDob = (
  date,
  currentFormat,
  requiredFormat = 'MMM DD, yyyy',
  countryCode = 'US', // default locale
) => {
  if (typeof date === 'number') date = date * 1000;

  if (countryCode === 'GB') {
    requiredFormat = requiredFormat || 'DD MMM yyyy';
  }
  if (!requiredFormat) {
    requiredFormat = 'MMM DD, yyyy';
  }
  return moment.utc(date, currentFormat).format(requiredFormat);
};

export const normalizeGender = (gender) => {
  const value = gender && gender[0].toUpperCase() + gender.slice(1).toLowerCase();
  if (value && !['Male', 'Female'].includes(value)) return 'Others';
  return value;
};

export const formatUTC = (date, requiredFormat = 'yyyy-MM-DD') => {
  // Note : the date should be in the utc format
  return moment.utc(date).format(requiredFormat);
};

// Format milliseconds / MM-DD-yyyy to required format - Used to display in UI
export const formatDate = (
  date,
  currentFormat,
  requiredFormat = 'MMM DD, yyyy',
  countryCode = 'US',
) => {
  if (countryCode === 'GB') {
    requiredFormat = requiredFormat || 'DD MMM yyyy';
  }
  if (!requiredFormat) {
    requiredFormat = 'MMM DD, yyyy';
  }
  return moment.utc(date, currentFormat).local().format(requiredFormat);
};

// Formate from timestamp to DD-MM-yy HH:mm
export const formatDateTimeFromUnix = (
  timeStamp,
  requiredFormat = 'MMM DD, yyyy HH:mm',
) => moment.unix(timeStamp).format(requiredFormat);

export const convertDlinkToPreview = (url) => {
  const isGoogleDriveLink = url.includes('drive.google.com/file/d/');

  if (isGoogleDriveLink) {
    return url.replace('/view', '/preview');
  } else {
    return false;
  }
};

export const formatWithUtcOffset = (date, requiredFormat = 'MMM DD, yyyy') => {
  console.log(date, requiredFormat);
  let formattedDate = date;
  try {
    const timeDiff = moment.parseZone(date, '').utcOffset();

    // Date 2022-10-17 14:00:00+00:00 should be treated as local slots
    // eventhough timeDiff is 0
    if (timeDiff === 0 && !date.includes('+00:00'))
      formattedDate = moment(date).format(requiredFormat);
    else
      formattedDate = moment(date).utcOffset(+timeDiff).format(requiredFormat);
  } catch (err) {
    console.log(err);
  }

  return formattedDate;
};

const formatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
});

const mainCurrencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
});

export const formatCurrency = (amount = 0, countryConfig = {}) => {
  // Extract currency and locale from countryConfig, fallback to defaults
  const currencyCode = countryConfig?.currency?.code || 'USD';
  const locale =
    countryConfig?.currency?.format || countryConfig?.locale || 'en-US';

  // Create a dynamic formatter based on country configuration
  const dynamicFormatter = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: 2,
  });

  return dynamicFormatter.format(amount);
};

export const formatCount = (count = 0) => count.toLocaleString('en-US');

export const formatMainCurrency = (amount = '', countryConfig = {}) => {
  // Extract currency and locale from countryConfig, fallback to defaults
  const currencyCode = countryConfig?.currency?.code || 'USD';
  const locale =
    countryConfig?.currency?.format || countryConfig?.locale || 'en-US';

  // Create a dynamic formatter based on country configuration
  const dynamicMainCurrencyFormatter = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: 0,
  });

  return dynamicMainCurrencyFormatter.format(amount);
};

export const getFileExtension = (fileName) => {
  const extensionMatch = fileName.match(/\.[0-9a-z]+$/i);

  return extensionMatch ? extensionMatch[0] : '';
};

export const getImageProperties = (file) => {
  return new Promise((resolve, reject) => {
    if (!file) reject(new Error('file not present'));

    const img = new Image();
    const binaryData = [];
    binaryData.push(file);
    img.src = URL.createObjectURL(
      new Blob(binaryData, { type: 'application/zip' }),
    );
    img.onload = () => {
      const width = img?.naturalWidth;
      const height = img?.naturalHeight;
      resolve({ width, height });
    };
    img.onerror = () =>
      reject(new Error('failed to upload the image, please try again'));
  });
};

export const formatZipcode = (inputValue, countryConfig) => {
  if (!inputValue) return '';

  const input = inputValue.toString();

  // Handle US zipcode formatting
  if (countryConfig?.code === 'US') {
    const digitsOnly = input?.replace(/\D/g, '');
    return digitsOnly?.slice(0, 5);
  }

  // Handle UK postcode formatting
  if (countryConfig?.code === 'GB') {
    const cleanInput = input?.replace(/[^a-zA-Z0-9]/g, '')?.toUpperCase();
    if (cleanInput?.length === 0) return '';

    // Max 7 chars without space
    const limitedInput = cleanInput?.slice(0, 7);
    let formatted = '';

    // Correct UK postcode pattern
    // Outward: A9, A9A, A99, AA9, AA9A, AA99 | Inward: 9AA
    const ukPattern = /^([A-Z]{1,2}[0-9][0-9A-Z]?)\s?([0-9][A-Z]{2})$/;

    const match = limitedInput?.match(ukPattern);

    if (match) {
      const [, outward, inward] = match;
      formatted = outward + ' ' + inward || '';
    } else {
      // Partial fallback: if we at least have 5 chars, try to split
      if (limitedInput?.length >= 5) {
        const firstPart = limitedInput?.slice(0, -3);
        const lastPart = limitedInput?.slice(-3);

        if (/^[0-9][A-Z]{2}$/.test(lastPart)) {
          formatted = firstPart + ' ' + lastPart || '';
        } else {
          formatted = limitedInput;
        }
      } else {
        formatted = limitedInput;
      }
    }

    return formatted;
  }
  // Fallback for other countries
  return input?.replace(/\D/g, '');
};

export const formatNPI = (inputValue) =>
  inputValue?.toString()?.replace(/\D/g, '')?.slice(0, 10);

export const formatSSN = (inputValue) =>
  inputValue?.toString()?.replace(/\D/g, '')?.slice(0, 4);

export const formatFacilityId = (inputValue) =>
  inputValue
    ?.toString()
    ?.replace(/[^0-9a-zA-Z]/g, '')
    ?.slice(0, 8);

export const _calculateDaysDiff = (pDate) => {
  const pastDate = moment(pDate);
  const currDate = moment();

  return currDate?.diff(pastDate, 'days');
};

export const calculateStripeCharges = (amount, cardType = '') => {
  if (!amount) return 0;

  if (cardType) {
    let fixedPercent = 0;
    let fixedCents = 0;

    if (cardType === 'credit') {
      fixedPercent = REACT_APP_CREDIT_CHARGE_PERCENT;
      fixedCents = REACT_APP_CREDIT_CHARGE_FIXED_CENTS;
    } else {
      fixedPercent = REACT_APP_DEBIT_CHARGE_PERCENT;
      fixedCents = REACT_APP_DEBIT_CHARGE_FIXED_CENTS;
    }
    const finalAmt = amount + fixedCents / 100 || 0;
    return (amount && calculatePercentageDiscount(finalAmt, fixedPercent)) || 0;
  }

  const divisor = ((1 - REACT_APP_STRIPE_CHARGE_PERCENT / 100) * 100).toFixed(
    1,
  );

  let finalAmt = +((amount * 100) / divisor).toFixed(2);

  return finalAmt + parseInt(REACT_APP_STRIPE_CHARGE_FIXED_CENTS) / 100;
};

export const downloadFile = (data, type, filename) => {
  const blob = new Blob([data], { type });

  fileSaver.saveAs(blob, filename);
};

export const getPaymentDetails = (data) => {
  const {
    card_detail = '',
    card_details = '',
    payment_method_info = '',
  } = data;

  if (!payment_method_info) {
    return card_detail || card_details;
  } else {
    return payment_method_info;
  }
};

export const getCptDescription = (
  singleBenefit,
  removeFrequency = false,
) => {
  let description;

  if (singleBenefit?.cpt_display === 'plan_card') {
    description = singleBenefit?.description;
  } else if (singleBenefit.frequency === 'unlimited') {
    description = `Unlimited ${singleBenefit.description}`;
  } else {
    description = removeFrequency
      ? `${singleBenefit.description}`
      : `${singleBenefit.description} x ${singleBenefit.total_unit}`;
  }

  return description;
};

export const getCurrentYear = () => {
  return moment().year();
};

// Region / country routing utilities live in a dependency-free module so they
// can be unit-tested in isolation. Re-exported here to preserve `from 'utils'`.
export * from './region';

export const filterDisplayFields = (filters) => {
  if (!filters || typeof filters !== 'object') {
    return filters;
  }

  const {
    partner_name,
    group_name,
    region_name,
    location_name,
    // Filter out internal logic fields that shouldn't be sent to API
    ignoreGroupFilterInReport,
    ignoreLocationFilterInReport,
    ...apiFilters
  } = filters;

  return apiFilters;
};

export const getArrayValue = (value, defaultValue = []) => {
  return Array.isArray(value) ? value : defaultValue;
};

export const sanitizeFileName = (value = '') =>
  value
    .replaceAll(/[^\w\s]/g, '')   // remove special chars (&, |, etc)
    .trim()
    .replaceAll(/\s+/g, '_');

/**
 * Sort discount categories by `category_order` ascending. When two categories
 * share the same `category_order`, fall back to comparing `range_start` so
 * the rows render in a stable, predictable order.
 */
export const sortCategoriesByOrder = (categories = []) => {
  const parseRangeStart = (value) => {
    const raw = (value ?? '').toString().trim();
    const num = Number(raw);
    return { raw, num, isNumeric: raw !== '' && !Number.isNaN(num) };
  };

  return [...categories].sort((a, b) => {
    const orderDiff = (a?.category_order ?? 0) - (b?.category_order ?? 0);
    if (orderDiff !== 0) return orderDiff;

    const aStart = parseRangeStart(a?.range_start);
    const bStart = parseRangeStart(b?.range_start);

    if (aStart.isNumeric && bStart.isNumeric) {
      return aStart.num - bStart.num;
    }
    return aStart.raw.localeCompare(bStart.raw);
  });
};

/**
 * Assign `category_order` to each category based on its current position in
 * the array (1-indexed). Called at save time so the backend always receives a
 * definitive display order matching the UI — even when the backend originally
 * returned no `category_order` values, or returned the same order for every
 * row. Any existing `category_order` value is overridden by the UI position.
 */
export const assignCategoryOrderByPosition = (categories = []) =>
  categories.map((category, index) => ({
    ...category,
    category_order: index + 1,
  }));

// Query string for API params, dropping anything unset. Shared so the
// services that talk to appcore build one the same way.
export const buildQuery = (params = {}) => {
  const entries = Object.entries(params).filter(([, value]) => value != null);
  return entries.length ? `?${new URLSearchParams(entries).toString()}` : '';
};
